import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  map,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { TimelineApiService } from './timeline-api.service';
import {
  TimelineActionInfoMessageDone,
  TimelineActionInfoMessageDoneFailed,
  TimelineActionInfoMessageDoneSuccess,
  TimelineActionLoadTimelineFailed,
  TimelineActionLoadTimelineSuccess,
  TimelineActions,
  TimelineActionTypes,
  TimelineActionLoadTimeline,
  TimelineActionCheckAndScheduleNotification
} from './timeline.actions';
import { Store, select } from '@ngrx/store';
import {
  CoreActionOpenQuickTour,
  CoreActionsTypes,
  CoreQuickTourStepTypes,
  selectorCoreLogin
} from '@bepatient/core';
import { QuickTourComponent } from 'libs/common/src/lib/quick-tour/quick-tour.component';
import { ModalController } from '@ionic/angular';
import { NotificationService, SharedUtilsService } from '@bepatient/shared';
import { PendingResult } from '@capacitor/local-notifications';

@Injectable()
export class TimelineEffects {
  private actions$ = inject(Actions);
  private timelineApiService = inject(TimelineApiService);
  private store$ = inject<Store<any>>(Store);
  private sharedUtilsService = inject(SharedUtilsService);
  private notificationService = inject(NotificationService);
  private modalController = inject(ModalController);

  loadTimeline$: Observable<TimelineActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(TimelineActionTypes.LOAD_TIMELINE),
      switchMap(() =>
        this.timelineApiService.loadTimeline().pipe(
          switchMap(res =>
            of(
              new TimelineActionCheckAndScheduleNotification(res),
              new TimelineActionLoadTimelineSuccess(res)
            )
          ),
          catchError(() => of(new TimelineActionLoadTimelineFailed()))
        )
      )
    )
  );

  timelineSuccess$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TimelineActionTypes.LOAD_TIMELINE_SUCCESS),
        delay(500),
        tap(() =>
          this.store$
            .pipe(select(selectorCoreLogin), take(1))
            .subscribe(login => {
              const quickTour = localStorage.getItem('quickTour');
              const displayQuickTour =
                !['done', 'skipped'].includes(quickTour) &&
                !login.userIsInOnboarding &&
                !login.userMustEnterOnboarding &&
                login.isDisplayQuickTour;
              if (displayQuickTour) {
                this.store$.dispatch(new CoreActionOpenQuickTour());
              }
            })
        )
      ),
    { dispatch: false }
  );

  infoMessageDone$: Observable<TimelineActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(TimelineActionTypes.INFO_MESSAGE_DONE),
      map((action: TimelineActionInfoMessageDone) => action.payload),
      switchMap(infoMessage =>
        this.timelineApiService.infoMessageDone(infoMessage).pipe(
          map(() => new TimelineActionInfoMessageDoneSuccess(infoMessage)),
          catchError(() =>
            of(new TimelineActionInfoMessageDoneFailed(infoMessage))
          )
        )
      )
    )
  );

  infoMessageDoneSuccess$: Observable<TimelineActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(TimelineActionTypes.INFO_MESSAGE_DONE_SUCCESS),
      map((action: TimelineActionInfoMessageDoneSuccess) => action.payload),
      filter(payload => payload.resource.type.toString() !== 'activity-close'),
      map(() => new TimelineActionLoadTimeline())
    )
  );

  // Check and schedule Notification
  // concatMap runs one sync at a time, so two timeline loads close together
  // cannot read the same pending list and both schedule the notifications
  checkAndScheduleNotifications$: Observable<PendingResult | undefined> =
    createEffect(
      () =>
        this.actions$.pipe(
          ofType(TimelineActionTypes.CHECK_AND_SCHEDULE_NOTIFICATION),
          map(
            (action: TimelineActionCheckAndScheduleNotification) =>
              action.payload
          ),
          // Windows of pages with the ± option use device-local time, as the other pages
          concatMap(timeline =>
            from(
              this.notificationService.syncTimelineNotifications(
                timeline.rrules_info || []
              )
            ).pipe(
              catchError(error => {
                console.error('Error scheduling notifications:', error);
                return of(undefined);
              })
            )
          )
        ),
      { dispatch: false }
    );

  // Effect to open quick tour when triggered
  openQuickTour$ = createEffect(
    () => () =>
      this.actions$.pipe(
        ofType(CoreActionsTypes.OPEN_QUICK_TOUR),
        switchMap(async () => {
          const modal = await this.modalController.create({
            component: QuickTourComponent,
            canDismiss: true,
            animated: true,
            mode: 'ios',
            showBackdrop: true,
            componentProps: {
              // This steps decide what type of quick tour will be presented
              steps: [
                CoreQuickTourStepTypes.INTRO,
                CoreQuickTourStepTypes.TIMELINE,
                CoreQuickTourStepTypes.MENU,
                CoreQuickTourStepTypes.ACCOUNT
              ]
            },
            cssClass: 'modal-fullscreen'
          });
          await modal.present();
          modal.onDidDismiss().then(() => {
            // Do something after modal dismiss
          });
        })
      ),
    { dispatch: false }
  );
}
